// 고객사 기준 정보 — 오른쪽 «고객사 기준 상세» 카드(기본 정보 · 센터별 명부 연결 · 운영 기준 · 운영 기준 수정 · 종료/복구).
// 상세 응답은 부모(WorkCustomersPage)가 읽어 넘긴다 — 바꾸면 onChanged() 로 부모가 상세·목록·변경이력을 다시 읽는다.
// 🔴 단추는 응답의 can_edit(본사 편집)·editable_fields 로만 정한다(서버가 정본 · 이미 403/404).
import { useEffect, useMemo, useState } from 'react';
import { workCustomersAPI } from '../../api/client';
import { alertDialog, promptDialog, toast } from '../../components/AppDialog';
import { Button } from '../../components/ui';
import { fmtKstDateTime } from '../../utils/kstTime';
import { COLORS, badge, card, input } from '../../utils/uiStyles';
import { KIND_OPTIONS, STATUS_ENDED, dash, errorText, isStale } from './workCustomerLabels';

const sectionTitle = { margin: '16px 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.text };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 16px' };
const labelStyle = { fontSize: '12px', color: COLORS.textMuted };
const valueStyle = { fontSize: '13px', color: COLORS.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
const fieldLabel = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub };
const SKIP_BASIC = new Set(['name', 'no']);

function Info({ label, children, wide }) {
  return (
    <div data-testid="work-customer-info" style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
      <span style={labelStyle}>{label}</span>
      <div style={valueStyle}>{children}</div>
    </div>
  );
}

// 센터 행 하나 — 원문 + 미매칭 배지 + 명부 이름 · 본사 편집이고 매칭된 행이면 [명부 연결].
function CenterRow({ customerId, row, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const current = row.shipper_id == null ? '' : String(row.shipper_id);

  const start = async () => {
    setOpen(true);
    setChoice(current);
    setCandidates(null);
    try {
      const res = await workCustomersAPI.shipperCandidates(customerId, row.id);
      setCandidates(res && res.data && Array.isArray(res.data.items) ? res.data.items : []);
    } catch (e) {
      setOpen(false);
      alertDialog(errorText(e, '명부 후보를 불러오지 못했습니다'));
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      await workCustomersAPI.linkShipper(customerId, row.id, { shipper_id: choice === '' ? null : Number(choice) });
      toast(choice === '' ? '명부 연결을 해제했습니다' : '명부를 연결했습니다');
      setOpen(false);
      onChanged();
    } catch (e) {
      alertDialog(errorText(e, '명부를 연결하지 못했습니다'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="work-customer-center" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', padding: '3px 0' }}>
      <span style={{ fontWeight: 500 }}>{row.center_raw}</span>
      {!row.perf_center && <span style={badge('warn')}>센터 미매칭</span>}
      <span style={{ fontSize: '12px', color: COLORS.textSub }}>{row.shipper_name ? `명부: ${row.shipper_name}` : '명부 미연결'}</span>
      {canEdit && row.perf_center && !open && (
        <Button variant="secondary" size="sm" onClick={start}>명부 연결</Button>
      )}
      {open && (
        <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {candidates === null ? (
            <span style={{ fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</span>
          ) : (
            <select aria-label={`${row.center_raw} 명부`} value={choice} onChange={(e) => setChoice(e.target.value)}
              style={{ ...input, width: 'auto', padding: '4px 8px', fontSize: '12px' }}>
              <option value="">연결 해제</option>
              {candidates.map((c) => <option key={c.id} value={String(c.id)}>{c.display_name}</option>)}
            </select>
          )}
          <Button size="sm" onClick={save} disabled={busy || candidates === null || choice === current}>저장</Button>
          <Button variant="quiet" size="sm" onClick={() => setOpen(false)} disabled={busy}>취소</Button>
        </span>
      )}
    </div>
  );
}

export default function CustomerDetailCard({ customerId, detail, error, onChanged }) {
  const [field, setField] = useState('');
  // null = 아직 손대지 않음(그때 [기준 수정]은 꺼져 있다 — 빈 칸이 «비우기»로 나가지 않게).
  const [value, setValue] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const editable = useMemo(() => (detail && Array.isArray(detail.editable_fields) ? detail.editable_fields : []), [detail]);
  const fieldMap = useMemo(() => {
    const map = {};
    (detail && Array.isArray(detail.fields) ? detail.fields : []).forEach((f) => { map[f.key] = f; });
    return map;
  }, [detail]);

  // 고객사를 바꾸면 수정 폼을 처음으로 — 항목은 고칠 수 있는 첫 항목.
  useEffect(() => {
    setValue(null);
    setReason('');
    setField('');
  }, [customerId]);
  const activeField = editable.includes(field) ? field : (editable[0] || '');

  if (customerId == null) {
    return (
      <section data-testid="work-customer-detail" aria-label="고객사 기준 상세" style={{ ...card, minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>고객사 기준 상세</h2>
        <p style={{ fontSize: '12px', color: COLORS.textMuted }}>고객사를 고르면 운영 기준이 보입니다.</p>
      </section>
    );
  }
  if (error) {
    return (
      <section data-testid="work-customer-detail" aria-label="고객사 기준 상세" style={{ ...card, minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>고객사 기준 상세</h2>
        <div role="alert" style={{ marginTop: '8px', fontSize: '13px', color: COLORS.dangerText }}>{error}</div>
      </section>
    );
  }
  if (!detail || !detail.customer || detail.customer.id !== customerId) {
    return (
      <section data-testid="work-customer-detail" aria-label="고객사 기준 상세" style={{ ...card, minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>고객사 기준 상세</h2>
        <div role="status" style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div>
      </section>
    );
  }

  const c = detail.customer;
  const ended = c.status === STATUS_ENDED;
  const canEdit = !!detail.can_edit;
  const fields = Array.isArray(detail.fields) ? detail.fields : [];
  const centers = Array.isArray(detail.centers) ? detail.centers : [];
  const stamp = fmtKstDateTime(c.updated_at || c.created_at);
  const subtitle = `${dash(c.centers_raw)} · 최종 수정 ${stamp || '—'}${c.updated_by_name ? ` (${c.updated_by_name})` : ''}`;

  const current = activeField && fieldMap[activeField] && fieldMap[activeField].value != null ? String(fieldMap[activeField].value) : '';
  const next = value === null ? null : value.trim();
  const isKind = activeField === 'kind';
  const same = next === null || next === current.trim();

  const submit = async () => {
    if (!activeField || same || busy) return;
    setBusy(true);
    try {
      const res = await workCustomersAPI.patchField(c.id, {
        field: activeField, value: next === '' ? null : next, before: current, reason: reason.trim(),
      });
      if (res && res.data && res.data.changed === false) toast('바뀐 내용이 없습니다', 'info');
      else toast('수정했습니다');
      setValue(null);
      setReason('');
      onChanged();
    } catch (e) {
      if (isStale(e)) {
        await alertDialog(errorText(e, '다른 사람이 먼저 바꿨습니다. 다시 불러온 뒤 수정하세요.'));
        onChanged();
      } else {
        alertDialog(errorText(e, '수정하지 못했습니다'));
      }
    } finally {
      setBusy(false);
    }
  };

  const endCustomer = async () => {
    const why = await promptDialog(`'${c.name}' 고객사를 종료합니다. 종료 사유를 입력하세요(선택).`, '', { danger: true, okText: '종료' });
    if (why === null || why === undefined) return;
    try {
      await workCustomersAPI.end(c.id, { reason: String(why).trim() });
      toast('종료했습니다');
      onChanged();
    } catch (e) {
      alertDialog(errorText(e, '고객사를 종료하지 못했습니다'));
    }
  };
  const restoreCustomer = async () => {
    try {
      await workCustomersAPI.restore(c.id, {});
      toast('복구했습니다');
      onChanged();
    } catch (e) {
      alertDialog(errorText(e, '고객사를 복구하지 못했습니다'));
    }
  };

  return (
    <section data-testid="work-customer-detail" aria-label="고객사 기준 상세" style={{ ...card, minWidth: 0 }}>
      <div style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: '4px' }}>고객사 기준 상세</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>{c.name}</h2>
        {ended && <span data-testid="work-customer-ended" style={badge('gray')}>종료</span>}
        {canEdit && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px' }}>
            {ended
              ? <Button variant="secondary" size="sm" onClick={restoreCustomer}>복구</Button>
              : <Button variant="danger" size="sm" onClick={endCustomer}>고객사 종료</Button>}
          </span>
        )}
      </div>
      <p data-testid="work-customer-subtitle" style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textSub }}>{subtitle}</p>

      <h3 style={sectionTitle}>기본 정보</h3>
      <div style={grid}>
        <Info label="상태">{dash(c.status)}</Info>
        {fields.filter((f) => f.group === 'basic' && !SKIP_BASIC.has(f.key)).map((f) => {
          if (f.key === 'centers_raw') {
            return (
              <Info key={f.key} label={f.label} wide>
                {centers.length === 0
                  ? <span>{dash(f.value)}</span>
                  : centers.map((row) => (
                    <CenterRow key={row.id} customerId={c.id} row={row} canEdit={canEdit} onChanged={onChanged} />
                  ))}
              </Info>
            );
          }
          if (f.key === 'center_staff') {
            return <Info key={f.key} label={f.label}>{f.value ? f.value : '미지정'}</Info>;
          }
          return <Info key={f.key} label={f.label}>{dash(f.value)}</Info>;
        })}
      </div>

      <h3 style={sectionTitle}>운영 기준</h3>
      <div data-testid="work-customer-ops" style={grid}>
        {fields.filter((f) => f.group === 'ops').map((f) => <Info key={f.key} label={f.label}>{dash(f.value)}</Info>)}
      </div>

      {editable.length > 0 && (
        <div data-testid="work-customer-edit" style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${COLORS.borderSoft}` }}>
          <h3 style={{ ...sectionTitle, marginTop: 0 }}>운영 기준 수정</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
            <label style={fieldLabel}>
              항목
              <select aria-label="수정할 항목" value={activeField}
                onChange={(e) => { setField(e.target.value); setValue(null); }} style={{ ...input, padding: '6px 8px' }}>
                {editable.map((k) => <option key={k} value={k}>{fieldMap[k] ? fieldMap[k].label : k}</option>)}
              </select>
            </label>
            <label style={fieldLabel}>
              현재 값
              <input aria-label="현재 값" value={current} readOnly style={{ ...input, background: COLORS.borderSoft }} />
            </label>
            <label style={fieldLabel}>
              변경 후 값
              {isKind ? (
                <select aria-label="변경 후 값" value={value === null ? current : value} onChange={(e) => setValue(e.target.value)} style={{ ...input, padding: '6px 8px' }}>
                  <option value="">비움</option>
                  {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              ) : (
                <input aria-label="변경 후 값" value={value === null ? '' : value} onChange={(e) => setValue(e.target.value)} style={input} />
              )}
            </label>
            <label style={fieldLabel}>
              사유
              <input aria-label="사유" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="변경 사유(선택)" style={input} />
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <Button onClick={submit} disabled={busy || same}>{busy ? '수정 중…' : '기준 수정'}</Button>
            <span style={{ fontSize: '12px', color: COLORS.textMuted }}>수정하면 아래 기준 변경이력에 자동으로 남습니다.</span>
          </div>
        </div>
      )}
    </section>
  );
}
